import { getAuthToken } from '../services/auth/tokenStorage';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { DeviceEventEmitter } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../config';

/** Event name used to trigger a notification count refresh from outside the React tree */
export const NOTIFICATION_REFRESH_EVENT = 'NOTIFICATION_REFRESH';

/**
 * Trigger a notification count refresh from anywhere (e.g., push notification foreground handler).
 * This fires a DeviceEventEmitter event that the NotificationProvider listens for.
 */
export const triggerNotificationRefresh = () => {
  DeviceEventEmitter.emit(NOTIFICATION_REFRESH_EVENT);
};

interface NotificationContextType {
  unreadCount: number;
  refreshUnreadCount: () => Promise<boolean>;
}

const NotificationContext = createContext<NotificationContextType>({
  unreadCount: 0,
  refreshUnreadCount: async () => true,
});

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0);

  /** Resolves true when the API answered (or there was no session to ask for). */
  const refreshUnreadCount = useCallback(async (): Promise<boolean> => {
    try {
      const token = await getAuthToken();
      if (!token) {
        setUnreadCount(0);
        return true;
      }

      const response = await fetch(`${API_URL}/notifications/unread-count`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.count || 0);
      }
      return true;
    } catch (error) {
      // Silently fail - notification count is non-critical
      // Just keep the count at 0
      setUnreadCount(0);
      return false;
    }
  }, []);

  useEffect(() => {
    // Poll every 30 seconds while the API answers. When it does not (the
    // device is offline, or a browser tab was left open against a server
    // that went away), back off up to five minutes so an idle tab does not
    // keep hammering the network; the next success resets the pace.
    const BASE_MS = 30000;
    const MAX_MS = 5 * 60 * 1000;
    let delay = BASE_MS;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;

    const tick = async () => {
      const ok = await refreshUnreadCount();
      if (stopped) return;
      delay = ok ? BASE_MS : Math.min(delay * 2, MAX_MS);
      timer = setTimeout(tick, delay);
    };
    tick();

    // Listen for push notification refresh events from outside the React tree
    const eventSubscription = DeviceEventEmitter.addListener(
      NOTIFICATION_REFRESH_EVENT,
      () => {
        refreshUnreadCount();
      },
    );

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      eventSubscription.remove();
    };
  }, [refreshUnreadCount]);

  return (
    <NotificationContext.Provider value={{ unreadCount, refreshUnreadCount }}>
      {children}
    </NotificationContext.Provider>
  );
};
